import * as React from 'react';
import type { HelpTopic } from '@chess/api-help';

// The help topic behind the card's "?": what each kind of position is and
// how a middlegame's score change is shared among them. The rules are
// `positionTypesOf` in `@chess/api-insights`; registered by this plugin's setup.
export const POSITIONS_HELP = 'middlegame.positions';

export const positionsHelp: HelpTopic = {
  title: 'Positions you misplay',
  body: (
    <>
      <p>
        Each bar is a kind of middlegame position. Its length is your average score change, in pawns, per middlegame of the last 30 days in which you played
        that kind of position: red where you lost ground in it, green where you gained. The worst kind comes first, and the card lists the <b>worst five</b>. Clicking a bar opens those
        games.
      </p>
      <p>
        A kind is listed only when it came up in at least <b>5</b> of your middlegames: with fewer, one bad game decides its average, so it would rank above
        kinds you played a hundred times on the strength of a single blunder. The note says when some kinds are not listed.
      </p>
      <h3>How the score change is shared</h3>
      <p>
        Every one of your middlegame moves takes the score change since your previous move (the opponent's move and yours), from your side. That change goes
        to every kind of the position you played the move in, so a move in a closed centre with queens on counts for both. All your moves together add up to
        the middlegame's score change. A game counts in a kind only if you made at least 3 moves in it, so a passing moment, such as a trade half done, does
        not.
      </p>
      <h3>The kinds</h3>
      <ul>
        <li><b>Queens on</b>: both sides have a queen. <b>Queens off</b>: neither has.</li>
        <li><b>Open centre</b>: at most two pawns, of both sides, on the d- and e-files, and none of them blocked.</li>
        <li><b>Closed centre</b>: a pawn on the d- or e-file stands blocked, nose to nose with an enemy pawn.</li>
        <li><b>Opposite castling</b>: one king on the queenside (files a–c), the other on the kingside (files g–h).</li>
        <li>
          <b>Your king exposed</b>: the opponent has a queen and your king has left its first two ranks, or has fewer than two of your pawns on the three files
          around it, one and two squares ahead.
        </li>
        <li><b>Your bishop pair</b>: you have both bishops and the opponent does not. <b>Their bishop pair</b>: the reverse.</li>
        <li><b>Exchange up</b>: you have a rook more and a bishop or knight fewer. <b>Exchange down</b>: the reverse.</li>
      </ul>
      <p>A position can be of several kinds at once, or of none (a centre with three or more pawns and none blocked is neither open nor closed).</p>
    </>
  ),
};
